import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useBreakpointDown } from '@/hooks/use-breakpoint';
import Modal from '@/components/common/modal';
import { useTranslations } from '@/i18n/use-translations';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import { useCreateOrganizationMutation } from '@/state/mutations/organizations/use-create-organization';
import { useUpdateUserMutation } from '@/state/mutations/users/use-update-user';
import { useNotifications } from '@/hooks/use-notifications';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useGetQuestionsQuery } from '@/state/queries/users/use-get-questions';
import type { z } from 'zod';
import { userOnboardingSchema } from '@/schemas/user/onboarding-schema';
import type { QuestionAnswerDto } from '@/types/dtos/user/request/question-answer.dto';
import type { UpdateUserDto } from '@/types/dtos/user/request/update-user.dto';
import britestageLogo from '@/assets/britestage-logo.png';

type FormData = z.infer<typeof userOnboardingSchema>;

interface OrganizationFormProps {
  formData: FormData;
  setFormData: (data: FormData) => void;
  answersMap: Record<string, string[]>;
  commentsMap: Record<string, Record<string, string>>;
  onBack: () => void;
  onNext: () => void;
  teamName: string;
  setTeamName: (name: string) => void;
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  formData,
  answersMap,
  commentsMap,
  onBack,
  onNext,
  teamName,
  setTeamName,
}) => {
  const t = useTranslations();
  const isMobile = useBreakpointDown('sm');

  const notification = useNotifications();
  const { setActiveOrganization } = useActiveOrganizationStore();
  const { data: allQuestions } = useGetQuestionsQuery();

  const { mutateAsync: createOrgAsync, isPending: creatingOrg } = useCreateOrganizationMutation();
  const { mutateAsync: updateUserAsync, isPending: updatingUser } = useUpdateUserMutation();

  const [submitted, setSubmitted] = useState(false);

  const handleContinue = async () => {
    if (!teamName.trim() || submitted) return;
    setSubmitted(true);

    try {
      const questionAnswers: QuestionAnswerDto[] = [];
      for (const q of allQuestions || []) {
        for (const answerId of answersMap[q.id] ?? []) {
          const answer = q.answers.find((a) => a.id === answerId);
          const userComment = answer?.commentRequired ? commentsMap[q.id]?.[answerId] : undefined;
          questionAnswers.push({
            questionId: q.id,
            answerId,
            value: true,
            userComment,
            updatedAt: new Date().toISOString(),
          });
        }
      }

      const payload: UpdateUserDto = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        language: formData.language,
        isOnboarded: true,
        cookiePreferences: {
          functional: true,
          analytical: false,
          marketing: false,
        },
        questionAnswers,
      };

      await updateUserAsync(payload);
      const org = await createOrgAsync({ name: teamName });

      if (org) {
        setActiveOrganization(org);
        await updateUserAsync({
          activeOrganization: org.id,
        });
      }
      setTimeout(() => onNext(), 200);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      notification.notifyError(`${t('onBoarding.notifications.errorNotification')}: ${message}`);
      setSubmitted(false);
    }
  };

  const loading = submitted && (creatingOrg || updatingUser);

  return (
    <Modal
      open
      hideCloseIcon
      title={t('onBoarding.organizationForm.title')}
      description={t('onBoarding.organizationForm.description')}
      size="md"
      fullScreen={isMobile}
      width={isMobile ? undefined : '450px'}
      height={isMobile ? undefined : '650px'}
      footer={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onBack}
            startIcon={<ChevronLeftOutlinedIcon />}
            disabled={loading}
          >
            {t('common.back')}
          </Button>
          <Button variant="contained" color="info" onClick={handleContinue} disabled={!teamName.trim() || loading}>
            {t('common.confirm')}
          </Button>
        </Box>
      }
    >
      <Box sx={{ textAlign: 'center', py: 2, maxHeight: '100%', overflow: 'auto' }}>
        <img src={britestageLogo} alt="Britestage Logo" style={{ marginBottom: 24, width: '200px', height: '47px' }} />
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('onBoarding.organizationForm.eventDesc')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontWeight: 'bold' }}>
            {t('onBoarding.organizationForm.organizationDesc')}
          </Typography>
        </Box>
        <TextField
          fullWidth
          variant="standard"
          label={t('onBoarding.organizationForm.label')}
          placeholder={t('onBoarding.organizationForm.placeHolder')}
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          helperText={t('onBoarding.organizationForm.helperText')}
        />
      </Box>
    </Modal>
  );
};

export default OrganizationForm;
